import { useState } from 'react'
import { useGetMany, useGetList, useNotify } from 'react-admin'
import { useFormContext, useWatch } from 'react-hook-form'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import Autocomplete from '@mui/material/Autocomplete'
import MuiTextField from '@mui/material/TextField'
import DeleteIcon from '@mui/icons-material/Delete'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import SaveIcon from '@mui/icons-material/Save'

const AssignStoryAside = ({ storyIds, onAdd }) => {
  const notify = useNotify()
  const [searchText, setSearchText] = useState('')
  const [selected, setSelected] = useState(null)

  const { data: searchResults = [] } = useGetList('kids-stories', {
    filter: searchText ? { title: { $iLike: `%${searchText}%` } } : {},
    pagination: { page: 1, perPage: 20 },
    sort: { field: 'title', order: 'ASC' },
  })

  const options = searchResults.filter(s => !storyIds.includes(s.id))

  const handleAdd = () => {
    if (!selected) return
    if (storyIds.includes(selected.id)) {
      notify('Story already added', { type: 'warning' })
      return
    }
    onAdd(selected)
    setSelected(null)
    setSearchText('')
  }

  return (
    <Box sx={{ margin: '0.5rem', width: 400, flexShrink: 0 }}>
      <Box sx={{ p: 2 }}>
        <Autocomplete
          options={options}
          getOptionLabel={option => option.title || ''}
          value={selected}
          onChange={(_, value) => setSelected(value)}
          inputValue={searchText}
          onInputChange={(_, value) => setSearchText(value)}
          filterOptions={x => x}
          renderInput={params => (
            <MuiTextField
              {...params}
              label="Select Story"
              variant="standard"
              fullWidth
            />
          )}
        />
      </Box>
      <Box sx={{ p: 2, pt: 0 }}>
        <Button
          type="button"
          variant="contained"
          size="small"
          startIcon={<SaveIcon />}
          disabled={!selected}
          onClick={handleAdd}>
          Save
        </Button>
      </Box>
    </Box>
  )
}

const KidSeriesStoriesInput = ({
  source = 'stories',
  storyIdsSource = 'storyIds',
}) => {
  const { setValue } = useFormContext()
  const seriesStories = useWatch({ name: source }) || []
  const orderedEntries = [...seriesStories].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0),
  )
  const storyIds = orderedEntries.map(s => s.id)

  const { data: fetchedStories = [] } = useGetMany(
    'kids-stories',
    { ids: storyIds },
    { enabled: storyIds.length > 0 },
  )

  const storiesMap = {}
  fetchedStories.forEach(s => {
    if (s) storiesMap[s.id] = s
  })

  const updateSources = entries => {
    setValue(source, entries, { shouldDirty: true })
    setValue(
      storyIdsSource,
      entries.map(e => e.id),
      { shouldDirty: true },
    )
  }

  const handleAdd = story => {
    const newEntries = [
      ...orderedEntries,
      { id: story.id, order: orderedEntries.length + 1 },
    ]
    updateSources(newEntries)
  }

  const handleRemove = id => {
    const filtered = orderedEntries
      .filter(e => e.id !== id)
      .map((e, i) => ({ ...e, order: i + 1 }))
    updateSources(filtered)
  }

  const handleDragEnd = result => {
    if (!result.destination) return
    const items = Array.from(orderedEntries)
    const [moved] = items.splice(result.source.index, 1)
    items.splice(result.destination.index, 0, moved)
    updateSources(items.map((e, i) => ({ ...e, order: i + 1 })))
  }

  return (
    <Box sx={{ display: 'flex', width: '100%', alignItems: 'flex-start' }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="kid-series-stories">
            {provided => (
              <Table
                size="small"
                ref={provided.innerRef}
                {...provided.droppableProps}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 32 }} />
                    <TableCell>Story ID</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell sx={{ width: 48 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {storyIds.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          align="center"
                          sx={{ py: 2 }}>
                          No stories added yet
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {storyIds.map((id, index) => (
                    <Draggable
                      key={String(id)}
                      draggableId={String(id)}
                      index={index}>
                      {(dragProvided, snapshot) => (
                        <TableRow
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          sx={{
                            bgcolor: snapshot.isDragging
                              ? 'action.hover'
                              : 'inherit',
                          }}>
                          <TableCell
                            {...dragProvided.dragHandleProps}
                            sx={{ cursor: 'grab', color: 'text.disabled' }}>
                            <DragIndicatorIcon fontSize="small" />
                          </TableCell>
                          <TableCell>{id}</TableCell>
                          <TableCell>{storiesMap[id]?.title || '—'}</TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemove(id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </TableBody>
              </Table>
            )}
          </Droppable>
        </DragDropContext>
      </Box>

      <AssignStoryAside storyIds={storyIds} onAdd={handleAdd} />
    </Box>
  )
}

export default KidSeriesStoriesInput
